import type {
  AgentControllerService,
  TlxUserEntitlementAttributeValueResponseModel,
} from "@/api";
import { hasErrorCode } from "@/helpers/apiErrorDetail";
import {
  type ApplicationPlan,
  type CoverageChange,
  type CoverageSnapshot,
  type ExpectedCoverageChanges,
  type ReviewCohort,
  toolIdentity,
} from "@/libs/estateCounts";
import type { AgentAccessFlow } from "./TrustAIExecutiveService";

/**
 * TEMP stub service for the AI Agent Estate onboarding preview
 * (`isNewAgentOnboarding`).
 *
 * The backend spec does not exist yet, so this file declares the API surface
 * (types + method names) the mock registry resolves against. Method names follow
 * the generated convention (`list/get/...UsingGet`, `...UsingPost`) because the
 * mock layer resolves entries by `method.name`.
 *
 * TODO: delete after `yarn generate:specs` exposes the real service, then replace
 * imports of this file with the generated `@/api` service + DTOs.
 */

/* ------------------------------------------------------------------ vocab */

/**
 * Platform / technology an agent runs on. Deliberately disjoint from the
 * connected-resource vocabulary: SharePoint and an internal knowledge base are
 * things an agent *reaches*, not places it runs, and having a value in both
 * lists made the estate impossible to explain.
 */
export type AgentPlatform =
  | "MICROSOFT_FOUNDRY"
  | "MICROSOFT_365"
  | "OPENAI"
  | "DATABRICKS"
  | "SNOWFLAKE"
  | "AWS_AGENTCORE"
  | "GLEAN";

/** A resource an agent reaches. Stable display strings, used as filter values. */
export type AgentTechnology =
  | "SharePoint"
  | "Snowflake"
  | "Databricks"
  | "Internal Knowledge Base"
  | "GitHub"
  | "Microsoft 365"
  | "ServiceNow"
  | "Salesforce";

/**
 * The delivery initiative an agent was built for. Display strings rather than
 * an enum, exactly like `AgentTechnology`: projects are tenant-authored, so a
 * fixed vocabulary would be wrong the moment a customer names their own. This
 * is why `DIMENSION_LABEL_MAPS` is `Partial` — PROJECT has no label map and
 * falls back to the raw value.
 *
 * A project belongs to a business unit but is not scoped to one: `Client
 * Onboarding (KYC)` and `Trade Surveillance` each span two, so grouping by
 * PROJECT partitions the estate differently rather than renaming
 * BUSINESS_UNIT.
 */
export type AgentProject =
  | "Client Onboarding (KYC)"
  | "Wealth Advisory Portal"
  | "Trade Surveillance"
  | "Trading Platform Modernization"
  | "Research Publishing";

/** Business unit / department an agent belongs to, from discovered metadata. */
export type AgentBusinessUnit =
  | "HR"
  | "CUSTOMER_SUPPORT"
  | "FINANCE"
  | "ENGINEERING"
  | "ANALYTICS";

/**
 * What the agent is *intended* to do, classified from prompts, tools and
 * metadata. Distinct from `EstateAgent.purposeDescription`, which is the
 * free-text sentence, and from `businessUnit`, which is who owns it: a
 * CONTENT_CREATION agent can sit in Customer Support.
 */
export type AgentPurpose =
  | "ENGINEERING"
  | "ANALYTICS"
  | "RESEARCH"
  | "HR"
  | "FINANCE"
  | "CUSTOMER_SUPPORT"
  | "CONTENT_CREATION";

/**
 * Policy-based access security status - whether the required policy controls
 * are in place to prevent unnecessary access to protected data and resources.
 *
 *  - SECURE:        every required access policy is applied.
 *  - NOT_SECURE:    one or more required controls are missing. There is no
 *                   "partial" state; the missing controls are named in detail
 *                   rather than softened into a third status.
 *  - NOT_EVALUATED: discovery has found the agent but classification has not
 *                   reached it yet, so no judgement can honestly be reported.
 *
 * SECURE is narrowly scoped to the controls TrustLogix evaluates. It is not a
 * claim that the agent is universally safe, compliant or vulnerability-free.
 */
export type AgentSecurityStatus = "SECURE" | "NOT_SECURE" | "NOT_EVALUATED";

/** The dimensions the estate can be organised by. */
export type AgentGroupDimension =
  | "NONE"
  | "BUSINESS_UNIT"
  | "PROJECT"
  | "ENVIRONMENT"
  | "PLATFORM"
  | "CONNECTED_RESOURCE"
  | "PURPOSE"
  | "SECURITY_STATUS";

/**
 * Stages of the post-onboarding run. Discovery itself already happened inside
 * the wizard (see `AgentDiscovery`), so the run starts by importing the agents
 * the administrator selected rather than finding them again.
 */
export type DiscoveryStageId =
  | "IMPORTING"
  | "LINKING_MCP"
  | "RESOLVING_IDENTITY"
  | "METADATA"
  | "ANALYZING"
  | "CLASSIFYING"
  | "POLICY_GAPS"
  | "RECOMMENDATIONS";

/** PROPOSED (no backend spec): `FAILED` is the only value the run has not reported yet. */
export type DiscoveryStageStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETE"
  | "FAILED";

/** Lifecycle of one environment's discovery run. */
export type OnboardingLifecycle = "IN_PROGRESS" | "COMPLETE" | "FAILED";

export type AgentIdentityProvider =
  | "MICROSOFT_ENTRA_ID"
  | "OKTA"
  | "PING_IDENTITY"
  | "SAVIYNT"
  | "SAILPOINT"
  | "AWS_IAM"
  | "OTHER"
  | "CONFIGURE_LATER";

export type AgentEnvironmentType = "PRODUCTION" | "TEST" | "DEVELOPMENT";

export type EnvironmentHealth = "HEALTHY" | "WARNING" | "DISABLED";

/* --------------------------------------------------- platform / environment */

/**
 * Sample connection details the platform advertises, used to prefill the
 * wizard's form. Never includes a secret - the authorization token is always
 * the administrator's to supply.
 */
export interface AgentPlatformSampleConnection {
  connectionName: string;
  account: string;
  clientId: string;
}

/** One selectable platform on the wizard's first step. */
/**
 * Whether a catalog entry can be picked: never Coming Soon, and not switched
 * off for this mode. The one rule behind the cards, the import chooser's
 * supported list and the demo store's guard.
 */
export const isCatalogEntrySelectable = (entry: {
  availability: "AVAILABLE" | "BETA" | "COMING_SOON";
  selectable?: boolean;
}): boolean => entry.availability !== "COMING_SOON" && entry.selectable !== false;

export interface AgentPlatformCatalogEntry {
  id: AgentPlatform;
  name: string;
  description: string;
  recommended: boolean;
  availability: "AVAILABLE" | "BETA" | "COMING_SOON";
  selectable?: boolean;
  /** Identity provider TrustLogix can infer for this platform, when it can. */
  detectedIdentityProvider?: AgentIdentityProvider;
  sampleConnection: AgentPlatformSampleConnection;
}

export interface AgentEnvironment {
  id: string;
  name: string;
  platform: AgentPlatform;
  identityProvider: AgentIdentityProvider;
  environmentType: AgentEnvironmentType;
  businessContext?: string;
  health: EnvironmentHealth;
  /** Wall-clock discovery start. Every progress value is derived from this. */
  startedAt: string;
  totalAgents: number;
  /**
   * PROPOSED (no backend spec): the provider projects this onboarding was
   * scoped to, so the sync job can say what it is importing. Absent on
   * environments onboarded before project scoping.
   */
  projects?: AgentRegistryProject[];
}

/** PROPOSED (no backend spec): one project in the agent registry provider. */
export interface AgentRegistryProject {
  id: string;
  name: string;
}

export interface StartAgentOnboardingRequest {
  name: string;
  platform: AgentPlatform;
  identityProvider: AgentIdentityProvider;
  /** The connection's tier; the backend defaults it when absent. */
  environmentType?: AgentEnvironmentType;
  businessContext?: string;
  /**
   * The wizard's project-listing run. Single-use: onboarding consumes it, so a
   * repeated submission cannot start a second registry from the same run.
   */
  discoveryId?: string;
  /**
   * PROPOSED (no backend spec): the reviewed project scope. Every agent in
   * these projects is imported - the sync job reads the current inventory, so
   * there is no agent-level selection. Projects added later are not included.
   */
  projectIds?: string[];
}

export interface TestAgentEnvironmentConnectionRequest {
  platform: AgentPlatform;
  connectionName: string;
  account: string;
  clientId: string;
  authorizationToken: string;
}

export interface TestAgentEnvironmentConnectionResult {
  success: boolean;
  message: string;
}

/**
 * An identity-provider connection test.
 *
 * `reusedFromPlatformConnection` says the credentials came from the platform
 * connection already proved in the previous step, rather than being entered
 * again. The wizard derives those values at read time instead of copying them
 * into their own fields, so this flag is what the mock needs in order to know
 * it is not looking at hand-entered input.
 */
export interface TestAgentIdentityConnectionRequest {
  provider: AgentIdentityProvider;
  tenantId: string;
  clientId: string;
  clientSecret: string;
  reusedFromPlatformConnection: boolean;
}

/* ------------------------------------------------ pre-onboarding discovery */

/**
 * Lightweight discovery the wizard runs before anything is imported: enough
 * to list the provider's projects for scoping, never the import itself or the
 * policy analysis - those run in the sync job after onboarding starts.
 */
export type AgentDiscoveryStageId = "CONNECTING" | "LISTING_PROJECTS";

export interface StartAgentDiscoveryRequest {
  name: string;
  platform: AgentPlatform;
  environmentType?: AgentEnvironmentType;
}

export interface AgentDiscoveryStageProgress {
  id: AgentDiscoveryStageId;
  status: DiscoveryStageStatus;
  value: number;
  total: number;
}

/**
 * PROPOSED (no backend spec): one accessible project, as the project-listing
 * run reads it. `agentCount` is a preview figure - the sync job may import a
 * different number - and is absent when the provider did not report it.
 */
export interface DiscoveredProject extends AgentRegistryProject {
  agentCount?: number;
}

/** PROPOSED (no backend spec): one row of a project's read-only agent preview. */
export interface ProjectAgentPreviewItem {
  id: string;
  name: string;
}

/**
 * PROPOSED (no backend spec). `complete: false` means the provider returned
 * only part of the project's agents - the preview says so rather than passing
 * a partial list off as the whole project.
 */
export interface ProjectAgentPreview {
  agents: ProjectAgentPreviewItem[];
  complete: boolean;
}

export interface AgentDiscovery {
  discoveryId: string;
  status: "RUNNING" | "COMPLETE";
  stages: AgentDiscoveryStageProgress[];
  /**
   * PROPOSED (no backend spec): every project the connection can read. Filled
   * once `status` is COMPLETE; empty while running. Not paginated - the list
   * is the provider's complete answer.
   */
  projects: DiscoveredProject[];
}

/* ------------------------------------------------------- MCP connectivity */

export type McpServiceStatus = "ACTIVE" | "OFFLINE";

/* ---------------------------------------------------------------- discovery */

export interface DiscoveryStageProgress {
  id: DiscoveryStageId;
  status: DiscoveryStageStatus;
  /** Items processed by this stage so far - agents, or MCP Services for LINKING_MCP. */
  value: number;
  total: number;
}

/**
 * One snapshot of the discovery run. `discoveredCount` is the estate as it
 * stands right now - the agent list and the category cards show exactly these
 * agents, so a card total and the progress panel can never disagree.
 */
export interface AgentOnboardingProgress {
  environment: AgentEnvironment;
  lifecycle: OnboardingLifecycle;
  /** Agents the run will find in total. */
  totalAgents: number;
  discoveredCount: number;
  /** Agents CLASSIFYING has reached. `discovered - classified` are NOT_EVALUATED. */
  classifiedCount: number;
  secureCount: number;
  notSecureCount: number;
  notEvaluatedCount: number;
  /** Distinct technologies seen across the discovered agents. */
  technologyCount: number;
  /** Discovered agents with at least one policy recommendation available. */
  recommendationCount: number;
  percentComplete: number;
  elapsedMs: number;
  /**
   * PROPOSED (no backend spec): when the run finished or failed. Absent while
   * it is running; derivable as `environment.startedAt + elapsedMs` otherwise.
   */
  completedAt?: string;
  /** PROPOSED (no backend spec): why the run failed, for the activity panel only. */
  failureMessage?: string;
  stages: DiscoveryStageProgress[];
  /**
   * TrustLogix enforcement readiness over the agents discovered so far. Part
   * of the polled snapshot, so the completion summary and the estate below it
   * count the same agents.
   */
  enforcement?: EnforcementReadinessCounts;
}

/* ------------------------------------------------------------------- agents */

/** Why TrustLogix classified an agent the way it did. */
export interface AgentClassificationEvidence {
  /** Excerpt only - the full system prompt is never surfaced by default. */
  systemPromptExcerpt: string;
  registeredTools: string[];
  dataSource: string;
  metadata: Array<{ label: string; value: string }>;
  applicationTags: string[];
}

/* ------------------------------------------------- enforcement readiness */

/**
 * Whether TrustLogix can enforce policies on an agent's runtime traffic.
 *
 * Deliberately separate from `AgentSecurityStatus` (policy coverage) and from
 * `EnforcementStatus` (how far one Intent Policy is realized at its enforcement
 * points): a policy can exist, be assigned and read as covered while the
 * agent's traffic never passes a TrustLogix enforcement path at all.
 *
 * `UNKNOWN` is a real answer - readiness could not be verified - and must never
 * be folded into `SETUP_REQUIRED` or counted as ready.
 */
export type EnforcementReadinessStatus =
  | "READY"
  | "SETUP_REQUIRED"
  | "ATTENTION_REQUIRED"
  | "UNKNOWN";

export type EnforcementMethod = "MCP_GATEWAY" | "PLUGIN" | "OTHER" | "NONE";

export type EnforcementHealth = "HEALTHY" | "UNHEALTHY" | "UNKNOWN";

export type EnvironmentEnforcementStatus =
  | "NOT_ENABLED"
  | "CONFIGURATION_REQUIRED"
  | "ENABLED"
  | "ATTENTION_REQUIRED"
  | "UNKNOWN";

/** Why an agent is not READY - an `agentEstate.enforcementReadiness.reason` key. */
export type EnforcementReadinessReason =
  | "noEnvironment"
  | "directMcp"
  | "notRouted"
  | "gatewayUnhealthy"
  | "mcpConnectionMissing"
  | "mcpConnectionFailed"
  | "unverifiable";

/**
 * What is known about one required path's setup. `UNKNOWN` (could not be read)
 * and `NOT_APPLICABLE` (the path contract says setup is not required) are
 * distinct from each other and from `SETUP_REQUIRED` - a missing field is never
 * defaulted to any of them.
 */
export type EnforcementPathState =
  | "CONFIGURED"
  | "SETUP_REQUIRED"
  | "UNKNOWN"
  | "NOT_APPLICABLE";

/**
 * One required path in an agent's enforcement setup: the routing of the agent
 * itself, or the gateway's connection to one MCP service the agent uses. An
 * agent is only fully configured when every required path is.
 */
export interface EnforcementPathFact {
  kind: "routing" | "mcpConnection";
  serviceId?: string;
  serviceName?: string;
  state: EnforcementPathState;
  /** Why the path is not configured, when it is known. */
  reason?: EnforcementReadinessReason;
}

export interface EnforcementReadiness {
  status: EnforcementReadinessStatus;
  method: EnforcementMethod;
  environmentConfigured: boolean;
  agentRoutedThroughEnforcement: boolean;
  /**
   * True only when the agent's runtime route was actually observed. A direct
   * (bypassing) connection is drawn only when this is true - an unverified
   * route is never presented as a known bypass.
   */
  routingObserved: boolean;
  healthStatus?: EnforcementHealth;
  reason?: EnforcementReadinessReason;
  /**
   * Each required path behind `status`. Absent when the source reports only an
   * agent-level answer; `status` is then the whole story and no path-level gap
   * can be shown.
   */
  paths?: EnforcementPathFact[];
  /** The enforcement environment the agent routes through, when it does. */
  configurationId?: string;
  environmentName?: string;
}

/** A partition of the same agents by readiness; sums to the agent total. */
export interface EnforcementReadinessCounts {
  ready: number;
  setupRequired: number;
  attentionRequired: number;
  unknown: number;
}

/** The tenant's configured enforcement path, independent of any one agent. */
export interface EnforcementEnvironment {
  status: EnvironmentEnforcementStatus;
  configured: boolean;
  enabled: boolean;
  method: EnforcementMethod;
  /** e.g. "Production MCP Gateway". */
  name?: string;
  healthStatus: EnforcementHealth;
  configurationId?: string;
  /**
   * Why an `UNKNOWN` status is unknown, when the read itself failed:
   * `FORBIDDEN` is the caller lacking access, `UNAVAILABLE` any other failure.
   * Never set on a successful read - an unconfigured gateway is `NOT_ENABLED`.
   */
  checkError?: "FORBIDDEN" | "UNAVAILABLE";
}

export interface EstateAgent {
  /** Absent means readiness could not be read - treat as UNKNOWN. */
  enforcementReadiness?: EnforcementReadiness;
  id: string;
  name: string;
  platform: AgentPlatform;
  businessUnit: AgentBusinessUnit;
  /** Fixed-vocabulary classification. */
  purpose: AgentPurpose;
  /** The free-text sentence describing what this agent does. */
  purposeDescription: string;
  connectedResource: AgentTechnology;
  project: AgentProject;
  /**
   * The agent's own deployment tier — NOT the connection's. One connected
   * environment hosts production, test and development agents alike, and
   * governance reads very differently across them.
   */
  environmentType: AgentEnvironmentType;
  region: string;
  owner: string;
  identityName: string;
  identityProvider: string;
  securityStatus: AgentSecurityStatus;
  /** How many recommendations currently apply. 0 means none can be offered. */
  recommendedPolicyCount: number;
  /** Policies applied to this agent, newest last. */
  appliedPolicyNames: string[];
  /**
   * Kill Switch: every resource access is suspended. Orthogonal to
   * `securityStatus` - the agent is not deleted and access can be restored.
   */
  accessRevoked: boolean;
  mcpServiceCount: number;
  toolCount: number;
  lastObserved: string;
  /** Undefined while the agent is NOT_EVALUATED. */
  lastEvaluated?: string;
  /**
   * The agent registry it was imported through, resolved against the
   * tenant's registries.
   */
  sourceIntegrationId?: string;
}

export type EstateAgentToolClassification =
  | "Read"
  | "Write"
  | "Destructive"
  | "Privileged";

export type EstateAgentToolRisk = "Low" | "Medium" | "High";

export type EstateAgentAccessState = "ALLOWED" | "RESTRICTED" | "DENIED";

export type EstateAgentProtectionStatus =
  | "PROTECTED"
  | "REVIEW"
  | "NEEDS_POLICY"
  | "RESTRICTED"
  | "DENIED";

export interface EstateAgentTool {
  dataSourceId?: string;
  /** The data source's presented name, where the relationship names one. */
  dataSourceName?: string;
  dataSourcePlatform?: string;
  /** The data source's `AccountTypes` key, for icon lookup - `dataSourcePlatform` is a display name. */
  dataSourcePlatformId?: string;
  dataDomains?: string[];
  id: string;
  name: string;
  /** What the tool does, where the source provides it. */
  description?: string;
  classification: EstateAgentToolClassification;
  risk: EstateAgentToolRisk;
  dataAccess: string;
  access: EstateAgentAccessState;
  protectionStatus: EstateAgentProtectionStatus;
  policyId?: string;
  policyName?: string;
}

export interface EstateAgentMcpService {
  id: string;
  name: string;
  /**
   * The connected technology this server fronts (Snowflake, SharePoint, ...).
   *
   * Carried explicitly rather than parsed back out of `name`: a server's
   * display name is a catalog label ("SharePoint MCP - Client Onboarding & KYC
   * Documents"), not a technology, so anything deriving the technology from it
   * breaks the moment a server is renamed.
   */
  technology: AgentTechnology;
  capabilities: string[];
  dataAccess: string[];
  protectionStatus: EstateAgentProtectionStatus;
  tools: EstateAgentTool[];
  /**
   * How this agent came to use the service: connected by an administrator
   * (`MANUAL`), or - absent or `DISCOVERED` - reported by the agent registry.
   * Only manual connections can be removed here; a discovered one would come
   * back with the next registry sync.
   */
  connectionOrigin?: McpConnectionOrigin;
  /** Manual connections: when and by whom. */
  connectedAt?: string;
  connectedBy?: string;
  /** The MCP Registry's id for this server, when the registry knows it. */
  registryServerId?: string;
}

export type McpConnectionOrigin = "DISCOVERED" | "MANUAL";

export interface EstateAgentActivity {
  /** Explicit governance evidence; a denied request alone is not a violation. */
  governanceViolation?: boolean;
  id: string;
  occurredAt: string;
  action: string;
  service: string;
  decision: "ALLOWED" | "DENIED" | "WARNING" | "RECORDED";
  kind?: "RUNTIME" | "POLICY_APPLIED" | "ACCESS_SUSPENDED" | "ACCESS_RESTORED";
  policyId?: string;
  requestId?: string;
  enforcementPoint?: string;
  policyName: string;
}

/* ----------------------------------------------------------------- mission */

/**
 * How far an agent's governed intent has progressed. Orthogonal to
 * `AgentSecurityStatus`: CONFIRMED says an administrator vouches for what the
 * agent is *for*, not that the controls around it are in place. An agent can be
 * SECURE with a mission nobody has reviewed, and vice versa.
 */
export type AgentMissionStatus =
  | "ADMINISTRATOR_DEFINED"
  | "INFERRED_NEEDS_REVIEW"
  | "CONFIRMED"
  | "OVERRIDDEN"
  | "NEEDS_MORE_CONTEXT"
  | "REVIEW_RECOMMENDED";

/** Who produced the mission text currently in force. */
export type AgentMissionSource =
  | "ADMINISTRATOR"
  | "IMPORTED"
  | "TRUSTAI_INFERRED"
  | "ADMINISTRATOR_OVERRIDE";

export type AgentMissionConfidenceLevel = "HIGH" | "MEDIUM" | "LOW";

/** Which discovery surface a piece of mission evidence came from. */
export type AgentMissionEvidenceType =
  | "IDENTITY"
  | "RUNTIME"
  | "MCP"
  | "TOOL"
  | "DATA"
  | "SEMANTIC";

export interface AgentMissionConfidence {
  level: AgentMissionConfidenceLevel;
  /** 0-100. Always rendered beside the level; the level never stands in for it. */
  score: number;
}

export interface AgentMissionEvidence {
  id: string;
  type: AgentMissionEvidenceType;
  /** The discovery surface, e.g. "Tool Definitions", "Semantic Model". */
  source: string;
  title: string;
  description: string;
  /** ISO-8601. Undefined when the source could not be read at all. */
  observedAt?: string;
  /** How many signals this one row stands for. Absent means 1. */
  signalCount?: number;
  /** Observed, but older than the freshness window. */
  stale?: boolean;
  /**
   * The source could not be read. The row is still returned so the gap in the
   * assessment is visible rather than silently absent.
   */
  unavailable?: boolean;
}

/** A mission TrustLogix considered but did not settle on. */
export interface AgentMissionCandidate {
  name: string;
  /** 0-100. */
  confidence: number;
  /** The signals that pointed at this candidate. */
  signals: string[];
}

export interface AgentMissionHistoryEntry {
  id: string;
  /** ISO-8601. */
  occurredAt: string;
  /** What happened. Administrator entries mirror `AgentMissionAction`. */
  action: string;
  detail: string;
  /** "TrustLogix" for inferred entries, an administrator name otherwise. */
  actor: string;
}

/** A capability discovered after the mission was last confirmed. */
export interface AgentMissionContextChange {
  id: string;
  /** The tool or data source that appeared. */
  name: string;
  service: string;
  /** Why it matters, e.g. "High-risk export". */
  classification: string;
}

/** What TrustLogix would call the mission now, offered after context drift. */
export interface AgentMissionSuggestion {
  name: string;
  description: string;
  purposeCategory: AgentPurpose;
  /** 0-100. */
  confidence: number;
}

export interface AgentMission {
  /**
   * The governed one-line intent. Undefined only under NEEDS_MORE_CONTEXT,
   * where naming a mission would be exactly the inference the assessment just
   * declined to make.
   */
  name?: string;
  /**
   * The SAME closed vocabulary as `EstateAgent.purpose`, deliberately not a
   * free-text category: `purpose` drives grouping, the list filter and every
   * recommendation selector, so a second free-text purpose on one record would
   * let the two disagree.
   */
  purposeCategory?: AgentPurpose;
  /** The governed sentence. `purposeDescription` remains the discovered one. */
  description?: string;
  status: AgentMissionStatus;
  source: AgentMissionSource;
  /** Withheld once a mission is governed - a confirmed mission is not a guess. */
  confidence?: AgentMissionConfidence;
  evidence: AgentMissionEvidence[];
  /** Only populated under NEEDS_MORE_CONTEXT. */
  candidates: AgentMissionCandidate[];
  /** One paragraph reconciling the evidence into the assessment. */
  synthesis?: string;
  /** ISO-8601. */
  confirmedAt?: string;
  confirmedBy?: string;
  /** ISO-8601. */
  lastReviewedAt?: string;
  /** Non-empty only under REVIEW_RECOMMENDED. */
  contextChanges: AgentMissionContextChange[];
  suggestedMission?: AgentMissionSuggestion;
  /** Newest first. */
  history: AgentMissionHistoryEntry[];
  /**
   * Mission inference could not run. The existing mission stays readable and
   * nothing on the page is blocked - the card says so rather than rendering an
   * empty assessment.
   */
  inferenceUnavailable?: boolean;
}

/**
 * What the administrator did. One action per call, and the value is also what
 * the history entry records - so the request and the audit trail can never
 * describe different things.
 *
 *  - CONFIRM:            accept the inferred mission as-is.
 *  - OVERRIDE:           replace it with the administrator's own text.
 *  - NEEDS_MORE_CONTEXT: decline to confirm; TrustLogix reassesses later.
 *  - KEEP_CURRENT:       context drift reviewed, mission unchanged.
 *  - ACCEPT_SUGGESTED:   adopt `suggestedMission` after context drift.
 */
/**
 * Statuses where an administrator has vouched for the mission.
 *
 * Lives beside the union rather than in either consumer: the mock store reads
 * it to decide whether to emit a confidence score and the UI reads it to decide
 * whether to render one, and a card offering "Confirm" on a mission with no
 * score is exactly what two copies drifting apart would produce.
 */
export const GOVERNED_MISSION_STATUSES: AgentMissionStatus[] = [
  "ADMINISTRATOR_DEFINED",
  "CONFIRMED",
  "OVERRIDDEN",
];

export type AgentMissionAction =
  | "CONFIRM"
  | "OVERRIDE"
  | "NEEDS_MORE_CONTEXT"
  | "KEEP_CURRENT"
  | "ACCEPT_SUGGESTED";

/** Supplied for OVERRIDE only; ignored for every other action. */
export interface AgentMissionOverride {
  name: string;
  purposeCategory: AgentPurpose;
  description: string;
  /** Why the administrator disagreed with the assessment. */
  reason?: string;
}

export interface UpdateAgentMissionRequest {
  environmentId: string;
  agentId: string;
  action: AgentMissionAction;
  mission?: AgentMissionOverride;
  /**
   * Who took the decision. Supplied by the caller from the session so the
   * history names a real person; the store never reaches for a user itself.
   */
  actor: string;
}

export interface UpdateAgentMissionResult {
  agentId: string;
  /** Echoed back so the caller can pick its snackbar without re-deriving it. */
  action: AgentMissionAction;
  mission: AgentMission;
}

/** A missing control, shared by coverage and its affected access path. */
export interface AgentPolicyGap {
  id: string;
  title: string;
  description: string;
  resource: string;
  toolNames: string[];
  recommendationId?: string;
}

export interface AgentPolicyAssignment {
  id: string;
  name: string;
  appliedAt: string;
}

/** One targeted agent, once, across every selected recommendation. */
export interface PolicyPreviewAgent {
  id: string;
  name: string;
  /** The selected recommendations that would newly be assigned to it. */
  policyIds: string[];
  before: CoverageSnapshot;
  after: CoverageSnapshot;
  change: CoverageChange;
}

export interface PolicyApplicationPreview {
  id: string;
  /**
   * Agents in the source scope. Legacy name: prefer `cohort.sourceAgentIds`.
   */
  scopeAgentCount: number;
  /**
   * Distinct agents the selected recommendations would newly be assigned to.
   * Legacy name: prefer `cohort.targetedAgentIds`.
   */
  affectedAgentCount: number;
  /**
   * Targeted agents projected to move from a policy gap to covered. Legacy
   * name: prefer `cohort.expectedCoverageChanges.newlyCovered`.
   */
  newlySecureCount: number;
  /** Who is in scope and who is not, by agent id. */
  cohort?: ReviewCohort;
  /** The distinct targeted agents, each with its projected coverage change. */
  agents?: PolicyPreviewAgent[];
  /**
   * The one plan review totals, execution and result reconciliation share:
   * distinct generated records (by logical id), what will happen to each, and
   * which agents depend on it.
   */
  applicationPlan?: ApplicationPlan;
  /**
   * Readiness of the agents the policies will be assigned to. Creating a
   * policy never depends on it - it decides whether the policy can control
   * runtime access once created.
   */
  enforcement?: EnforcementReadinessCounts;
  /** Every selected definition's native policies together, without double-counting servers. */
  implementationPlan?: PolicyImplementationPlan;
  policies: Array<{
    policyId: string;
    policyName: string;
    reason: string;
    outcome: string;
    /** Present for an Intent Policy: what it allows and restricts. */
    intent?: IntentPolicySummary;
    /** Where the intent is enforced for the agents this review assigns. */
    enforcementTargets?: EnforcementTargetSummary;
    /**
     * The enforcement points themselves - server and tool policies, data
     * controls - so the review can show where the intent will be enforced
     * before anything is applied.
     */
    enforcementPreview?: {
      mcpServers: McpEnforcement[];
      dataSources: DataEnforcement[];
    };
    /** The native policies applying this definition will create. */
    implementationPlan?: PolicyImplementationPlan;
    agents: Array<{
      id: string;
      name: string;
      gaps: AgentPolicyGap[];
      statusBefore: AgentSecurityStatus;
      statusAfter: AgentSecurityStatus;
      remainingGapCount: number;
    }>;
    excludedAgentCount: number;
    exclusions: Array<{
      id: string;
      name: string;
      reason: "NOT_APPLICABLE" | "ALREADY_ASSIGNED";
    }>;
    alreadyAppliedCount: number;
  }>;
}

export interface EstateAgentDetail extends EstateAgent {
  description: string;
  model: string;
  /**
   * The governed intent. Detail-only: the list has no mission column, and a
   * ten-field object on 149 rows would be paid for and never read.
   */
  mission: AgentMission;
  classificationEvidence: AgentClassificationEvidence;
  appliedPolicies: AgentPolicyAssignment[];
  policyGaps?: AgentPolicyGap[];
  mcpServices: EstateAgentMcpService[];
  recentActivity: EstateAgentActivity[];
  /** Undefined means the relationship record was unavailable, not no paths. */
  accessFlow?: AgentAccessFlow;
  /**
   * Intent Policies mapped to this agent, scoped to this agent alone - in
   * effect, or recommended and not yet applied (`applied: false`).
   */
  effectivePolicies?: EffectivePolicySummary[];
}

/** The real list params, plus `businessUnit` (no API param - backend will not add one; demo store only). */
export type EstateAgentListParams = NonNullable<
  Parameters<typeof AgentControllerService.listEstateAgentsUsingGet>[0]
> & { businessUnit?: string };

export interface EstateAgentQuery {
  environmentId: string;
  q?: string;
  platform?: string;
  businessUnit?: string;
  project?: string;
  environmentType?: string;
  connectedResource?: string;
  purpose?: string;
  securityStatus?: string;
  /** "true" | "false" - a string, so it survives the URL-param round trip. */
  hasRecommendations?: string;
  /**
   * An `EnforcementReadinessStatus`, `SETUP_GAP` (setup known to be
   * incomplete, unknown excluded) or `NOT_READY` for every status but READY. A string, like `securityStatus`, so it survives the URL round trip.
   */
  enforcementStatus?: string;
  /** Agents imported through this registry; in the demo, its environment id. */
  agentRegistryId?: string;
  /** 1-based, matching `usePaginateQuery`. */
  pageNo?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "ASC" | "DESC";
}

export interface PaginatedEstateAgents {
  items: EstateAgent[];
  pageNo: number;
  pageSize: number;
  total: number;
}

/* ------------------------------------------------------------ summary/cards */

/**
 * A partition of the same agents, always:
 * `secureCount + notSecureCount + notEvaluatedCount === totalAgents`.
 */
export interface EstateCoverage {
  totalAgents: number;
  secureCount: number;
  notSecureCount: number;
  notEvaluatedCount: number;
  /** Rounded 0-100 policy coverage: secure as a share of the total. */
  securePercent: number;
  /**
   * Agents with every TrustLogix control in place: policy-secure AND
   * enforcement READY. The rest are Unprotected. Optional until the backend
   * ships it - without it the client unions `attentionIds`, or reports a bound.
   */
  protectedCount?: number;
}

/**
 * The distinct agents behind each attention bucket, so a total can be a union
 * of ids instead of a sum of overlapping counts.
 *
 * Optional: it is a mock-store extension. A backend that returns counts only
 * leaves it out, and the client then reports bounds rather than a made-up union
 * (see `summarizeAttention`). Policy gap = NOT_SECURE; setup gap = enforcement
 * readiness SETUP_REQUIRED or ATTENTION_REQUIRED; `unknownReadiness` is kept
 * apart and is never a confirmed gap.
 */
export interface AttentionAgentIds {
  policyGap: string[];
  setupGap: string[];
  notEvaluated: string[];
  unknownReadiness: string[];
}

/**
 * Whether TrustLogix has a policy to recommend for a group's policy gap.
 * Explicit rather than inferred from `recommendedPolicyCount`: "nothing to
 * recommend" and "not enough information to recommend" are different answers.
 */
export type RecommendationState =
  | "AVAILABLE"
  | "NONE"
  | "INSUFFICIENT_INFORMATION";

/** One category card. */
export interface AgentEstateGroup extends EstateCoverage {
  /** Stable key for selection and routing, e.g. `PLATFORM:MICROSOFT_FOUNDRY`. */
  key: string;
  dimension: AgentGroupDimension;
  /** Raw filter value - what the agent list is filtered by. */
  value: string;
  /** Agents suspended by Kill Switch. Orthogonal to the three counts above. */
  accessSuspendedCount: number;
  /** Agents in this group with at least one recommendation available. */
  recommendationCount: number;
  /** Distinct remaining policies recommended for evaluated agents in this group. */
  recommendedPolicyCount: number;
  /** Whether a policy can be recommended for this group's gap. */
  recommendationState: RecommendationState;
  /** TrustLogix enforcement readiness - orthogonal to the policy coverage counts. */
  enforcement: EnforcementReadinessCounts;
  attentionIds?: AttentionAgentIds;
}

export interface AgentEstateSummary extends EstateCoverage {
  environmentId: string;
  dimension: AgentGroupDimension;
  accessSuspendedCount: number;
  recommendationCount: number;
  enforcement: EnforcementReadinessCounts;
  groups: AgentEstateGroup[];
  attentionIds?: AttentionAgentIds;
}

/* -------------------------------------------------------------------- scope */

/**
 * The agents an action targets - the ones the administrator actually chose. A
 * group scope resolves server-side to every agent in that group; there is no
 * further subsetting, so the count shown before applying is the count applied.
 */
export type AgentScope =
  | { kind: "ALL" }
  | { kind: "GROUP"; dimension: AgentGroupDimension; value: string }
  | { kind: "AGENTS"; agentIds: string[] }
  /**
   * Every agent matching a list filter. This is what a bulk action on a
   * filtered list actually means, and it is the only way to express a target
   * like "every agent that still has a recommendation waiting" without
   * enumerating ids the caller has not paged through.
   */
  | { kind: "FILTER"; filters: Omit<EstateAgentQuery, "environmentId"> };

/**
 * Stable, order-independent signature for a scope.
 *
 * Callers MUST use this as the react-query key for
 * `resolveAgentPolicyRecommendationsUsingPost`. The default key is
 * `flattenDeep([method.name, ...values(params)])`, which would splice every
 * agent id into the key array and re-key on every reorder of the selection.
 */
export function agentScopeSignature(scope: AgentScope): string {
  if (scope.kind === "ALL") return "ALL";
  if (scope.kind === "GROUP") return `GROUP:${scope.dimension}:${scope.value}`;
  if (scope.kind === "FILTER") {
    const entries = Object.entries(scope.filters)
      .filter(([, value]) => value !== undefined && value !== "")
      .sort(([left], [right]) => left.localeCompare(right));
    return `FILTER:${entries.map(([key, value]) => `${key}=${String(value)}`).join("&")}`;
  }

  const sorted = [...scope.agentIds].sort();
  let hash = 2166136261;
  for (const id of sorted) {
    for (let index = 0; index < id.length; index += 1) {
      hash ^= id.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
  }
  return `AGENTS:${sorted.length}:${(hash >>> 0).toString(36)}`;
}

/* ----------------------------------------------------------- recommendations */

export type PolicyCategory =
  | "PRIVACY_PROTECTION"
  | "REGULATORY_COMPLIANCE"
  | "BUSINESS_RULES"
  | "TOOL_ACTION_CONTROLS"
  | "DATA_ACCESS_CONTROLS";

export type RecommendationStrength = "STRONGLY_RECOMMENDED" | "RECOMMENDED";

export type PolicyControlSense = "ALLOW" | "BLOCK";

export interface PolicyControl {
  sense: PolicyControlSense;
  label: string;
}

/**
 * The mapping behind "View enforcement details" - how a recommendation becomes
 * a real platform control. Kept out of the card's default view on purpose.
 */
export interface PolicyEnforcementDetail {
  resource: string;
  toolGroups: string[];
  allowed: string[];
  blocked: string[];
  enforcementLayer: string;
}

export interface RecommendationScope {
  agentCount: number;
  /** Pre-composed, e.g. "8 Engineering agents · GitHub". */
  label: string;
}

export interface AgentPolicyRecommendation {
  id: string;
  /** Names the outcome, e.g. "Prevent destructive repository actions". */
  name: string;
  /** What happens once this is applied. */
  outcome: string;
  /** Why it was recommended, referencing purpose, technology or capability. */
  whyRecommended: string;
  controls: PolicyControl[];
  appliesTo: RecommendationScope;
  strength: RecommendationStrength;
  category: PolicyCategory;
  technology: AgentTechnology;
  frameworks: string[];
  enforcement: PolicyEnforcementDetail;
  /** Present for an Intent Policy: what it allows and restricts. */
  intent?: IntentPolicySummary;
  /** Where the intent would be enforced for the agents it applies to. */
  enforcementTargets?: EnforcementTargetSummary;
  /** Union of the policy's targets across the agents it applies to. */
  targets?: EnforcementCoverage;
  /** Readiness of the agents it applies to - orthogonal to coverage. */
  enforcementReadiness?: EnforcementReadinessCounts;
  /**
   * Recommended, but not a coverage requirement: it opens no policy gap and
   * never gates Policy Covered.
   */
  advisory?: boolean;
}

/* ---------------------------------------------------------- policy coverage */

/**
 * A policy's lifecycle on the agents it is assigned to. Deliberately not a
 * security posture: an active policy says nothing about whether the agent is
 * Secure - runtime verification is reported separately.
 */
export type AppliedPolicyState = "ACTIVE";

/** One policy already assigned to at least one agent in the scope. */
export interface AppliedPolicyCoverage {
  policyId: string;
  name: string;
  state: AppliedPolicyState;
  /**
   * The in-scope agents it is assigned to. Ids rather than a count, so the
   * coverage summary can union overlapping policies instead of summing them.
   */
  agentIds: string[];
  /** Allow/block controls, for a policy that is not an Intent Policy. */
  controls: PolicyControl[];
  /** Present for an Intent Policy: what it allows and restricts. */
  intent?: IntentPolicySummary;
  /** Where the intent is enforced for the agents it is assigned to. */
  enforcementTargets?: EnforcementTargetSummary;
}

/** The policies already applied across a scope - context, never a selection. */
export interface AgentPolicyCoverage {
  scopeAgentCount: number;
  policies: AppliedPolicyCoverage[];
}

/* ---------------------------------------------------------- policy catalog */

export interface PolicyCatalogItem {
  id: string;
  name: string;
  category: PolicyCategory;
  description: string;
  technology: AgentTechnology | "Any";
  /** Where it applies, e.g. "Engineering agents with repository write access". */
  scope: string;
  frameworks: string[];
  controls: PolicyControl[];
}

export interface PolicyCatalogQuery {
  q?: string;
  category?: string;
  technology?: string;
}

/* ------------------------------------------------------ policy applicability */

/**
 * How a policy relates to the agents in scope. Deliberately separate from
 * enforcement readiness: a policy can be applicable to agents TrustLogix cannot
 * yet enforce on, and a not-applicable policy says nothing about the runtime.
 *
 * - RECOMMENDED: TrustLogix identified it as appropriate for this context.
 * - APPLICABLE: it can be applied, but was not identified as a recommendation.
 * - NOT_APPLICABLE: the context or resources it requires are absent.
 */
export type PolicyApplicability = "RECOMMENDED" | "APPLICABLE" | "NOT_APPLICABLE";

/** Why a policy is not applicable: what it needs, and what was found. */
export interface PolicyRequirement {
  required: string;
  detected: string;
}

/** A catalog policy, read against a scope. */
export interface ScopedPolicyCatalogItem extends PolicyCatalogItem {
  applicability: PolicyApplicability;
  /** Present for NOT_APPLICABLE. */
  applicabilityReason?: string;
  requirement?: PolicyRequirement;
  /** Union of the policy's targets across the applicable agents in scope. */
  targets?: EnforcementCoverage;
  /** Readiness of the applicable agents in scope. */
  enforcement?: EnforcementReadinessCounts;
  /** Present when `applicability` is RECOMMENDED. */
  recommendationStrength?: RecommendationStrength;
  /** Present when `applicability` is RECOMMENDED - never composed otherwise. */
  whyRecommended?: string;
}

export interface ScopedPolicyCatalogQuery extends PolicyCatalogQuery {
  environmentId: string;
  scope: AgentScope;
}

/* ----------------------------------------------------------- policy review */

/** Whether the policy is already assigned to the agents it applies to. */
export type PolicyAssignmentState = "NOT_APPLIED" | "APPLIED" | "PARTIALLY_APPLIED";

/** One policy, as the canonical Policy Review workspace presents it. */
export interface PolicyReviewPolicy {
  policyId: string;
  name: string;
  category: PolicyCategory;
  /** A Custom Policy draft generated this session. */
  isDraft: boolean;
  /** True for an Intent Policy: it names allowed and restricted data. */
  intentBased: boolean;
  frameworks: string[];
  /** Only for a policy TrustLogix actually recommended in this scope. */
  recommendation?: { strength: RecommendationStrength; rationale: string };
  applicability: PolicyApplicability;
  applicabilityReason?: string;
  requirement?: PolicyRequirement;
  summary: string;
  intent: IntentPolicySummary;
  controls: PolicyControl[];
  policyState: PolicyAssignmentState;
  /** How the policy becomes a runtime control, e.g. "MCP server and tool policies". */
  enforcementMethod?: string;
  /** Before apply: the native policies applying will create for the applicable agents. */
  implementationPlan?: PolicyImplementationPlan;
}

/** One agent in the review's scope, for the policies under review together. */
export interface PolicyReviewAgent {
  id: string;
  name: string;
  applicability: PolicyApplicability;
  /** Policy ids from the review that apply to this agent. */
  policyIds: string[];
  readiness: EnforcementReadiness;
  targets: EnforcementCoverage;
  /**
   * The subset of `policyIds` this agent does not have yet - what applying
   * would newly assign. Empty when every applicable policy is already in place.
   */
  pendingPolicyIds?: string[];
  /**
   * The agent's MCP servers and tools could not be read, so whether these
   * policies reach it is unknown. It is neither targeted nor excluded.
   */
  applicabilityUnresolved?: boolean;
}

/**
 * Where one policy acts on the viewed agent. Ids match the agent's own
 * `mcpServices`, tool ids and `dataSourceId`s, so the Access Flow can scope to
 * them without translating anything.
 */
export interface PolicyTargets {
  policyId: string;
  mcpServiceIds: string[];
  /** `${serviceId}::${toolId}`. */
  toolKeys: string[];
  dataSourceIds: string[];
  dataControlCount: number;
}

export interface PolicyReviewQuery {
  environmentId: string;
  scope: AgentScope;
  policyIds: string[];
  /** The agent whose targets to return; defaults to the first applicable one. */
  agentId?: string;
}

export interface PolicyReview {
  scopeAgentCount: number;
  policies: PolicyReviewPolicy[];
  agents: PolicyReviewAgent[];
  viewedAgentId?: string;
  /** For `viewedAgentId`, one entry per policy that applies to it. */
  targets: PolicyTargets[];
  /**
   * Readiness across the TARGETED agents - those the policies would newly be
   * assigned to. All zeros when nothing would change.
   */
  enforcement: EnforcementReadinessCounts;
  /** Whose readiness `enforcement` tallies: agents newly reached, or agents that already have the policies. */
  enforcementScope?: "TARGETED" | "ASSIGNED";
  /** Source, candidate, targeted and excluded agents, by id. */
  cohort?: ReviewCohort;
}

export const policyToolKey = toolIdentity;

/* ------------------------------------------------------------ draft policy */

export interface DraftPolicyControl extends PolicyControl {
  /** The phrase in the intent that produced this control. */
  derivedFrom: string;
}

export interface DraftPolicy {
  /** Derived from the intent text, so the same sentence yields the same draft. */
  id: string;
  intent: string;
  /** Restatement of what the administrator asked for. */
  intentSummary: string;
  suggestedName: string;
  category: PolicyCategory;
  technology: AgentTechnology | "Any";
  controls: DraftPolicyControl[];
  /** Phrases that produced no control, so the UI can ask for clarification. */
  unmatchedTerms: string[];
}

export interface GenerateDraftPolicyRequest {
  environmentId: string;
  intent: string;
  scope?: AgentScope;
}

/* ------------------------------------------------------------------- apply */

export interface ApplyPolicyResult {
  policyId: string;
  policyName: string;
  appliedAt: string;
  /** Applicable agents receiving this policy in the reviewed assignment. */
  scopeAgentCount: number;
  /** Agents whose required evaluated gaps are now fully covered. */
  newlySecureCount: number;
  /** Where the intent is now enforced, for an Intent Policy. */
  enforcementTargets?: EnforcementTargetSummary;
  /** The single agent the policy became effective for, when there is one. */
  contextLabel?: string;
  /**
   * Readiness of the agents this one policy was assigned to - what decides
   * whether it now controls runtime access, or is only configured.
   */
  enforcement?: EnforcementReadinessCounts;
}

export interface ApplyPoliciesRequest {
  environmentId: string;
  policyIds: string[];
  scope: AgentScope;
  /** Required by the public mutation; optional for direct fixture setup. */
  previewId?: string;
}

/** Error code for an apply rejected because its preview went stale. */
export const SCOPE_CHANGED = "SCOPE_CHANGED";

/** Thrown by `applyPoliciesUsingPost` when `previewId` no longer matches. */
export class ScopeChangedError extends Error {
  readonly code = SCOPE_CHANGED;
  constructor(message = "The scope changed. Review the policies again.") {
    super(message);
    this.name = "ScopeChangedError";
  }
}

export const isScopeChangedError = (error: unknown): boolean =>
  hasErrorCode(error, SCOPE_CHANGED);

export interface ApplyPoliciesResult {
  appliedAt: string;
  results: ApplyPolicyResult[];
  /** Agents in the source scope the review was opened on. */
  sourceAgentCount?: number;
  /** Distinct agents the application newly reached: the original scope. */
  targetedAgentCount?: number;
  /**
   * @deprecated Same value as `targetedAgentCount`, under the name that read as
   * "the scope". Prefer `sourceAgentCount` / `targetedAgentCount`.
   */
  scopeAgentCount: number;
  /**
   * Targeted agents that ACTUALLY moved from a policy gap to covered, counted
   * once - read back from the committed state, not projected.
   */
  newlySecureCount: number;
  /** What the targeted agents actually became, by transition. */
  actualCoverageChanges?: ExpectedCoverageChanges;
  /** The plan the application executed: distinct records and their dependents. */
  plan?: ApplicationPlan;
  /** The original scope, so the result keeps describing it after coverage moves. */
  cohort?: ReviewCohort;
  /** Whole-estate policy coverage, so the UI can show the increase. */
  coverageBefore: EstateCoverage;
  coverageAfter: EstateCoverage;
  /** Readiness of the agents policies were assigned to. */
  enforcement?: EnforcementReadinessCounts;
  /** Each applied definition's implementation, as deployment starts. */
  implementations?: PolicyImplementation[];
}

export interface SetAgentAccessRevocationRequest {
  environmentId: string;
  agentIds: string[];
  accessRevoked: boolean;
}

export interface SetAgentAccessRevocationResult {
  agentIds: string[];
  accessRevoked: boolean;
}

/* ------------------------------------------------------------- enforcement */

/**
 * How far an Intent Policy's intent is realized at its enforcement points.
 * Orthogonal to `AgentSecurityStatus`: an agent can be Secure (the policy is
 * assigned) while one data control behind it is still Not mapped.
 */
export type EnforcementStatus =
  | "ENFORCED"
  | "PARTIALLY_ENFORCED"
  | "NOT_ENFORCED"
  | "NOT_MAPPED"
  | "CONFLICT_DETECTED";

/** The control points along an Agent -> MCP -> Tool -> Data path. */
export type EnforcementLayer = "AGENT" | "SERVER" | "TOOL" | "DATA";

/**
 * Where the Enforcement view was opened from. The same Intent Policy renders
 * against each; only the scope differs.
 */
export type EnforcementContext =
  | { type: "agent"; agentId: string }
  | { type: "group"; groupId: string }
  | { type: "policy" }
  | { type: "accessPath"; agentId: string; pathId: string };

export interface IntentPolicySummary {
  allow: string[];
  restrict: string[];
}

export interface IntentPolicy extends IntentPolicySummary {
  id: string;
  name: string;
  description: string;
}

export interface ToolEnforcement {
  id: string;
  name: string;
  classification: EstateAgentToolClassification;
  /** Inherited from the Server Access Policy, or a tool-specific rule. */
  mode: "inherit" | "custom";
  rule: string;
  status: EnforcementStatus;
}

export interface McpEnforcement {
  serverId: string;
  name: string;
  technology: AgentTechnology;
  serverPolicy: { name: string; status: EnforcementStatus };
  tools: ToolEnforcement[];
  customToolCount: number;
  status: EnforcementStatus;
  /** Agents in the current context that reach this server. */
  agentIds: string[];
  /** The MCP Registry record that owns this server's policy, when known. */
  registryServerId?: string;
}

export interface DataControl {
  id: string;
  name: string;
  description: string;
  status: EnforcementStatus;
  /** Why a control is not fully enforced, when it is not. */
  detail?: string;
}

export interface DataEnforcement {
  sourceId: string;
  name: string;
  platform: string;
  /** `platform`'s `AccountTypes` key, for icon lookup - `platform` is a display name. */
  platformId?: string;
  controls: DataControl[];
  status: EnforcementStatus;
  agentIds: string[];
}

export interface EnforcementCoverage {
  mcpServerCount: number;
  toolCount: number;
  dataSourceCount: number;
  controlCount: number;
}

export interface EnforcementTargetSummary extends EnforcementCoverage {
  mcpServers: Array<{ id: string; name: string; toolCount: number }>;
  dataSources: Array<{ id: string; name: string; controlCount: number }>;
}

export interface EnforcementHop {
  layer: EnforcementLayer;
  /** The resource at this hop, e.g. the server or data source name. */
  label: string;
  /** The policy or controls that apply at this hop. */
  control: string;
  status: EnforcementStatus;
}

export interface EnforcementAgentRef {
  id: string;
  name: string;
  businessUnit: AgentBusinessUnit;
  status: EnforcementStatus;
}

/** An enforcement point that does not fully realize the intent. */
export interface EnforcementFinding {
  id: string;
  layer: EnforcementLayer;
  status: EnforcementStatus;
  /** The server or data source the finding is about. */
  resource: string;
  /** The control or policy that is not fully enforced. */
  control: string;
  detail?: string;
  agentNames: string[];
}

export interface EffectiveEnforcement {
  policy: IntentPolicy;
  context: EnforcementContext;
  /** The agent's name in agent and path context; empty otherwise. */
  contextLabel: string;
  status: EnforcementStatus;
  /** Whether the policy is in effect for anything in this context. */
  applied: boolean;
  agents: { effective: EnforcementAgentRef[]; recommended: EnforcementAgentRef[] };
  coverage: EnforcementCoverage;
  mcpServers: McpEnforcement[];
  dataSources: DataEnforcement[];
  /** Only in `accessPath` context. */
  path?: { id: string; label: string; hops: EnforcementHop[] };
  findings: EnforcementFinding[];
}

/** An Intent Policy as a card: enough to show it and open its enforcement. */
export interface EffectivePolicySummary extends IntentPolicySummary {
  policyId: string;
  name: string;
  /**
   * False while the policy is only recommended. The card still shows where it
   * would be enforced, so the reader sees the targets before applying.
   */
  applied: boolean;
  status: EnforcementStatus;
  coverage: EnforcementCoverage;
  effectiveAgentCount: number;
}

/** Flattened so the react-query key stays a flat record of primitives. */
export interface EffectiveEnforcementQuery {
  environmentId: string;
  policyId: string;
  contextType: EnforcementContext["type"];
  agentId?: string;
  groupId?: string;
  pathId?: string;
}

export const toEnforcementQuery = (
  environmentId: string,
  policyId: string,
  context: EnforcementContext,
): EffectiveEnforcementQuery => ({
  environmentId,
  policyId,
  contextType: context.type,
  ...(context.type === "agent" ? { agentId: context.agentId } : {}),
  ...(context.type === "group" ? { groupId: context.groupId } : {}),
  ...(context.type === "accessPath"
    ? { agentId: context.agentId, pathId: context.pathId }
    : {}),
});

export const fromEnforcementQuery = (
  query: EffectiveEnforcementQuery,
): EnforcementContext | undefined => {
  switch (query.contextType) {
    case "agent":
      return query.agentId ? { type: "agent", agentId: query.agentId } : undefined;
    case "group":
      return query.groupId ? { type: "group", groupId: query.groupId } : undefined;
    case "accessPath":
      return query.agentId && query.pathId
        ? { type: "accessPath", agentId: query.agentId, pathId: query.pathId }
        : undefined;
    case "policy":
      return { type: "policy" };
    default:
      return undefined;
  }
};

/* --------------------------------------------------- policy implementation */

/*
 * A recommended or catalog policy is a *definition*: intended behavior. Applying
 * it materializes an *implementation* - the native policies that carry the
 * intent at each enforcement point:
 *  - MCP server and tool policies in the TrustLogix Policy Engine, enforced
 *    through the MCP Access Gateway;
 *  - data-platform controls, only where data-layer enforcement is required.
 *
 * Mock-only assumptions this section stands in for (no backend yet):
 *  - a relationship API from a definition to the native policies it generated,
 *    with stable child ids;
 *  - asynchronous create/deploy/verify status per native policy;
 *  - persisted individual overrides that never rewrite the parent definition;
 *  - reapply that refuses to overwrite overrides unless acknowledged;
 *  - retry of only the failed native policies.
 *
 * Deployment is not runtime enforcement: a fully deployed implementation still
 * controls nothing for an agent that bypasses the TrustLogix enforcement path.
 */

export type PolicyDefinitionState =
  | "RECOMMENDED"
  | "APPLYING"
  | "APPLIED"
  | "FAILED";

export type PolicyImplementationState =
  | "NOT_CREATED"
  | "CREATING"
  | "DEPLOYING"
  | "DEPLOYED"
  | "PARTIALLY_DEPLOYED"
  | "FAILED";

export type PolicyAlignmentState = "MATCHES_DEFINITION" | "MODIFIED" | "UNKNOWN";

export type NativePolicyState =
  | "QUEUED"
  | "CREATING"
  | "DEPLOYING"
  | "DEPLOYED"
  | "FAILED";

export type NativePolicyTarget = "MCP_SERVER" | "MCP_TOOL" | "DATA_PLATFORM";

export type NativePolicyEngine = "TLX_POLICY_ENGINE" | "DATA_PLATFORM";

export type NativePolicySource = "GENERATED" | "MANUALLY_MODIFIED";

export type NativePolicyEffect = "ALLOW" | "BLOCK" | "MASK" | "REQUIRE_APPROVAL";

/**
 * A native policy's logic, semantically: enough to say "BLOCK Finance Data"
 * against "ALLOW Finance Data" without diffing raw engine payloads.
 */
export interface NativePolicyLogic {
  effect: NativePolicyEffect;
  /** What the effect applies to, e.g. "Finance Data" or a tool's name. */
  subject: string;
  rule: string;
}

/** The server, tool or data source one native policy is attached to. */
export interface NativePolicyResource {
  id: string;
  name: string;
  /** MCP servers and tools: the technology the server fronts. */
  technology?: AgentTechnology;
  /** Data platforms: display name and `AccountTypes` key for the icon. */
  platform?: string;
  platformId?: string;
  /** Tools: their server and classification. */
  serverId?: string;
  serverName?: string;
  classification?: EstateAgentToolClassification;
}

export interface NativePolicy {
  /** Stable across reads, applies and reapplies of the same definition. */
  id: string;
  parentDefinitionId: string;
  parentDefinitionName: string;
  environmentId: string;
  name: string;
  target: NativePolicyTarget;
  engine: NativePolicyEngine;
  /** The server or data source this policy is grouped under. */
  groupId: string;
  /**
   * The enforcement point it carries, keyed as enforcement evaluation keys
   * targets: `serverId`, `serverId::toolId` or `sourceId:controlId`.
   */
  targetKey: string;
  resource: NativePolicyResource;
  state: NativePolicyState;
  source: NativePolicySource;
  /** What the applied definition generated. */
  expected: NativePolicyLogic;
  /** What the native policy currently says - differs only after an override. */
  current: NativePolicyLogic;
  failure?: string;
  createdAt?: string;
  deployedAt?: string;
  modifiedAt?: string;
  /** The MCP Registry record that owns the server, when known. */
  registryServerId?: string;
}

/** One MCP server (server + tool policies) or one data source (controls). */
export interface NativePolicyGroup {
  id: string;
  engine: NativePolicyEngine;
  name: string;
  technology?: AgentTechnology;
  platform?: string;
  platformId?: string;
  registryServerId?: string;
  state: PolicyImplementationState;
  serverPolicyCount: number;
  toolPolicyCount: number;
  controlCount: number;
  deployedCount: number;
  failedCount: number;
  modifiedCount: number;
  policies: NativePolicy[];
}

export type DeploymentStepKey =
  | "CREATE_TLX"
  | "DEPLOY_TLX"
  | "DEPLOY_PLATFORM"
  | "VERIFY";

export type DeploymentStepStatus = "PENDING" | "ACTIVE" | "DONE" | "FAILED";

export interface DeploymentStep {
  key: DeploymentStepKey;
  status: DeploymentStepStatus;
  /** Native policies the step covers in the current run. */
  policyCount: number;
  /** DEPLOY_PLATFORM: the data sources it deploys to. */
  resources?: string[];
}

export type DeploymentRunKind = "APPLY" | "REAPPLY" | "RETRY";

export type ImplementationEventKind =
  | "APPLIED"
  | "REAPPLIED"
  | "RETRIED"
  | "TLX_DEPLOYED"
  | "PLATFORM_DEPLOYED"
  | "DEPLOYMENT_FAILED"
  | "VERIFIED"
  | "MODIFIED";

export interface ImplementationEvent {
  id: string;
  occurredAt: string;
  kind: ImplementationEventKind;
  count?: number;
  /** The resource or native policy the event names. */
  label?: string;
}

export interface PolicyImplementationCounts {
  total: number;
  mcpServerPolicies: number;
  toolPolicies: number;
  /** Server and tool policies together - everything in the TLX engine. */
  tlxPolicies: number;
  dataControls: number;
  deployed: number;
  tlxDeployed: number;
  failed: number;
  modified: number;
}

export interface PolicyImplementation {
  definitionId: string;
  definitionName: string;
  environmentId: string;
  definitionState: PolicyDefinitionState;
  implementationState: PolicyImplementationState;
  alignment: PolicyAlignmentState;
  /** A deployment run is still creating, deploying or verifying. */
  inFlight: boolean;
  /** The latest deployment run, which `steps` describe. */
  run?: { id: string; kind: DeploymentRunKind; startedAt: string };
  steps: DeploymentStep[];
  groups: NativePolicyGroup[];
  counts: PolicyImplementationCounts;
  /** Native policies whose current logic no longer matches the definition. */
  modified: NativePolicy[];
  failed: NativePolicy[];
  /** Newest first. */
  history: ImplementationEvent[];
}

export interface PlannedMcpServer {
  id: string;
  name: string;
  technology: AgentTechnology;
  serverPolicyCount: number;
  toolPolicyCount: number;
}

export interface PlannedDataPlatform {
  sourceId: string;
  name: string;
  platform: string;
  platformId?: string;
  controlNames: string[];
}

/**
 * What applying one or more definitions will create, before anything exists.
 * `mcpServers` is distinct servers; the policy counts are per definition, so
 * two definitions on one server create two server policies on one server.
 */
export interface PolicyImplementationPlan {
  definitionIds: string[];
  mcpServers: PlannedMcpServer[];
  serverPolicyCount: number;
  toolPolicyCount: number;
  platforms: PlannedDataPlatform[];
  controlCount: number;
  /**
   * Of the policies counted above, those that already exist and will be
   * updated for the new agents rather than created.
   */
  updatedCount: number;
  /**
   * The rest of the classification, when the plan knows it. Created + updated
   * + unchanged + skipped + pending is the number of records counted above:
   * they are operations on the same records, never additional policies.
   */
  createdCount?: number;
  /** Already in place for exactly the agents under review: a successful no-op. */
  unchangedCount?: number;
  /** Carry an individual override, which applying leaves alone. */
  skippedCount?: number;
  /** Whether the record exists could not be read, so create-or-update is unknown. */
  pendingClassificationCount?: number;
}

export interface PolicyImplementationQuery {
  environmentId: string;
  policyId: string;
}

export interface PolicyImplementationsQuery {
  environmentId: string;
  policyIds: string[];
}

export interface UpdateNativePolicyRequest {
  environmentId: string;
  nativePolicyId: string;
  current: NativePolicyLogic;
}

export interface ReapplyPolicyDefinitionRequest {
  environmentId: string;
  policyId: string;
  /** Required when the implementation carries individual overrides. */
  acknowledgeOverrides?: boolean;
}

export interface RetryPolicyDeploymentRequest {
  environmentId: string;
  policyId: string;
}

export const OVERRIDES_PRESENT = "OVERRIDES_PRESENT";

/** Thrown by reapply when overrides exist and were not acknowledged. */
export class OverridesPresentError extends Error {
  readonly code = OVERRIDES_PRESENT;
  constructor(
    message = "This implementation has individual overrides. Review them before reapplying.",
  ) {
    super(message);
    this.name = "OverridesPresentError";
  }
}

export const isOverridesPresentError = (error: unknown): boolean =>
  hasErrorCode(error, OVERRIDES_PRESENT);

/* ----------------------------------------------------------------- service */

const mockOnly = (): never => {
  throw new Error(
    "AgentEstateService is mock-only - add a src/configs/mock.ts entry and a src/api/mock-data registry entry.",
  );
};

/**
 * IMPORTANT: declare these as regular static METHODS, not `static x = () => {}`.
 * The mock layer resolves registry entries by `method.name`, and at this repo's
 * `target: ES2020` a static arrow field compiles to `Svc.x = () => {}` - a member
 * assignment whose function has no inferred name - so the lookup silently misses
 * and every call resolves to `undefined`.
 */
export class AgentEstateService {
  static listAgentPlatformCatalogUsingGet(
    _params?: Record<string, never>,
  ): Promise<AgentPlatformCatalogEntry[]> {
    return Promise.resolve(mockOnly());
  }

  static testAgentEnvironmentConnectionUsingPost(
    _params: TestAgentEnvironmentConnectionRequest,
  ): Promise<TestAgentEnvironmentConnectionResult> {
    return Promise.resolve(mockOnly());
  }

  /**
   * Shares `TestAgentEnvironmentConnectionResult` with the platform test: the
   * answer is the same shape either way, and a second result type would only
   * duplicate the success/message pair.
   */
  static testAgentIdentityConnectionUsingPost(
    _params: TestAgentIdentityConnectionRequest,
  ): Promise<TestAgentEnvironmentConnectionResult> {
    return Promise.resolve(mockOnly());
  }

  static startAgentOnboardingUsingPost(
    _params: StartAgentOnboardingRequest,
  ): Promise<AgentEnvironment> {
    return Promise.resolve(mockOnly());
  }

  static disconnectAgentEnvironmentUsingDelete(_params: {
    id: string;
  }): Promise<{ success: boolean }> {
    return Promise.resolve(mockOnly());
  }

  /**
   * PROPOSED (no backend spec): a demo agent's entitlement attributes, in the
   * live `GET /api/user_entitlement_attribute/value` response shape. Live
   * agents read that endpoint; demo agents are not applications.
   */
  static listEstateAgentAttributesUsingGet(_params: {
    agentId: string;
  }): Promise<TlxUserEntitlementAttributeValueResponseModel> {
    return Promise.resolve(mockOnly());
  }

  /** PROPOSED: replace a demo agent's attribute record, whole. */
  static updateEstateAgentAttributesUsingPatch(_params: {
    agentId: string;
    values: Record<string, unknown>;
  }): Promise<TlxUserEntitlementAttributeValueResponseModel> {
    return Promise.resolve(mockOnly());
  }

  /**
   * The single polled endpoint. Embeds the environment on purpose so the polled
   * payload is the one source of truth for lifecycle - a separate
   * `getEnvironment` would be a second cache entry that can disagree with it.
   */
  static getAgentOnboardingProgressUsingGet(_params: {
    environmentId: string;
  }): Promise<AgentOnboardingProgress | undefined> {
    return Promise.resolve(mockOnly());
  }

  /**
   * Takes the same filters as the agent list, so the category cards and the
   * table narrow together when a summary metric is clicked.
   */
  static getAgentEstateSummaryUsingGet(
    _params: {
      environmentId: string;
      dimension: AgentGroupDimension;
    } & Omit<EstateAgentQuery, "environmentId">,
  ): Promise<AgentEstateSummary | undefined> {
    return Promise.resolve(mockOnly());
  }

  /**
   * The one mission write. Five administrator decisions, one endpoint: they all
   * mutate the same record, return the same shape, take the same
   * environment/agent pair, and each one's `action` is exactly what the history
   * entry records. Five verbs would be five copies of the same mutation hook,
   * the same snackbar wiring and the same mock entry.
   *
   * Also closer to what will actually ship: the real surface is a single
   * `PUT /agents/{id}/mission` with a discriminated body, not five RPCs.
   */
  static updateAgentMissionUsingPost(
    _params: UpdateAgentMissionRequest,
  ): Promise<UpdateAgentMissionResult> {
    return Promise.resolve(mockOnly());
  }

  /**
   * The tenant's runtime enforcement path. Stands in for a backend view over
   * the MCP Gateway configuration plus a health check.
   */
  static getEnforcementEnvironmentUsingGet(
    _params?: Record<string, never>,
  ): Promise<EnforcementEnvironment> {
    return Promise.resolve(mockOnly());
  }

  /** Starts the wizard's project-listing run against a tested connection. */
  static startAgentDiscoveryUsingPost(
    _params: StartAgentDiscoveryRequest,
  ): Promise<{ discoveryId: string }> {
    return Promise.resolve(mockOnly());
  }

  static getAgentDiscoveryUsingGet(_params: {
    discoveryId: string;
  }): Promise<AgentDiscovery> {
    return Promise.resolve(mockOnly());
  }

  /**
   * PROPOSED (no backend spec): one project's agents, read-only, for the
   * wizard's preview. Reads the project-listing run - it never starts the
   * onboarding import.
   */
  static listDiscoveredProjectAgentsUsingGet(_params: {
    discoveryId: string;
    projectId: string;
  }): Promise<ProjectAgentPreview> {
    return Promise.resolve(mockOnly());
  }
}
